import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, DestroyRef, ElementRef, HostListener, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { catchError, filter, map, of } from 'rxjs';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { I18nService, AppLang } from '../core/services/i18n.service';
import { NotificationApi, NotificationPage, NotificationView } from '../core/services/notification.service';
import { NOTIFICATION_FAMILLES, NotificationFamilleDef, familleDe } from '../core/notifications/notification-famille';
import { RoleService } from '../core/services/role.service';
import { ActionCountsApi } from '../core/services/action-counts.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { LucideDynamicIcon } from '@lucide/angular';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { CceTooltip } from '../shared/ui/tooltip/tooltip.directive';
import { SlideOver } from '../shared/ui/slide-over/slide-over';
import { GroupeRubriquesSource, RechercheGlobale } from '../shared/ui/recherche-globale/recherche-globale';
import { espaceDepuisUrl } from '../shared/navigation/espace-courant';
import { effacerRecents } from '../shared/ui/recherche-globale/recents';
import { NomUtilisateurService } from '../core/services/nom-utilisateur.service';
import { compteSuspendu } from '../core/services/compte-suspendu';


/** Entree de navigation (libelles AR + FR). Sert aux 8 espaces ET au menu contextuel. */
interface NavLink {
  readonly id: string;
  readonly ar: string;
  readonly fr: string;
  /** Route active (si l'ecran est deja implemente) ; sinon placeholder desactive. */
  readonly route?: string;
  /**
   * Paramètres de requête, séparés de la route.
   *
   * <p>Obligatoire, et non concaténés dans {@link route} : `[routerLink]` traite une chaîne comme un
   * chemin, donc « /x?y=z » viserait un segment « x?y=z » qui n'existe pas. Trois entrées du menu
   * mènent au même écran avec un `etape` différent — c'est ce paramètre qui les distingue.
   */
  readonly query?: Readonly<Record<string, string>>;
  readonly icon?: string;
  /**
   * Cède ses sous-écrans à une entrée plus précise du menu.
   *
   * <p>Nécessaire quand la route d'une entrée est le PRÉFIXE d'une autre : `/caeep/seances` l'est de
   * `/caeep/seances/pv`, si bien que les deux entrées s'allumaient ensemble (constaté à l'écran le
   * 2026-08-03) — le même défaut que `queryParams: 'exact'` corrige pour les trois files `etape`.
   * L'entrée reste allumée sur ses AUTRES sous-écrans : « برمجة الجلسة » ou le détail d'une séance
   * n'éteignent plus « جلسات اللجنة » (revue CAC du 2026-09-29, écran 27 — aucune entrée n'était
   * allumée). Seul le sous-arbre d'une autre entrée l'éteint (voir {@link MainLayout#cedeLeChemin}).
   * Non généralisé : la plupart des entrées n'ont pas de sœur plus précise.
   */
  readonly exact?: boolean;
  /**
   * Reste active QUEL QUE SOIT le `?etape=` de l'URL — l'inverse du `queryParams: 'exact'` des
   * entrées à query. Pour une entrée-chapeau dont les étapes sont des ONGLETS dans la page
   * (« دراسة الملفات ») : sans cela, cliquer un onglet éteindrait l'entrée du menu qui l'a ouverte.
   * Opt-in explicite, jamais le défaut : généraliser « subset » rallumerait le double-surlignage
   * corrigé le 2026-08-03 (`c-post-missions` s'allumait avec `c-post-rapport-*`).
   */
  readonly queryAgnostic?: boolean;
  /**
   * S'éteint quand l'URL porte ces paramètres, même si `queryAgnostic` l'allumerait (revue CAC du 2026-09-29, lot L4) :
   * sur le préréglage `?etape=DECISION`, seule « قرار التعيين » s'allume — deux entrées allumées disaient au menu deux
   * endroits pour un seul écran.
   */
  readonly eteinteSur?: Readonly<Record<string, string>>;
  /**
   * Clé de file « en attente de mon action » (ex. `president.rapports`) : si le backend renvoie un
   * compteur > 0 pour cette clé, une pastille est affichée sur l'entrée. Absent = jamais de pastille.
   */
  readonly badgeKey?: string;
  /**
   * Plusieurs files sous une même entrée : la pastille en fait la SOMME (« التواصل مع مراقبي الدولة » =
   * notes à décider + conversations non lues). Exclusif de `badgeKey`.
   */
  readonly badgeKeys?: readonly string[];
  /**
   * Autres racines d'URL où l'entrée reste allumée, sous-écrans compris : une rubrique à onglets dont chaque
   * onglet a sa route (hub « التواصل مع مراقبي الدولة »). Sans cela, seul le premier onglet l'allumerait.
   */
  readonly actifSur?: readonly string[];
  /**
   * Un simple filet AVANT l'entrée, sans titre : sépare des grappes d'entrées dans un groupe dont
   * l'ordre est imposé (« المتابعة », menu voulu par le Président le 2026-08-03). Décision Q11b du PO
   * (2026-09-29) : des filets, aucune entrée déplacée.
   */
  readonly separeAvant?: boolean;
}

/** Groupe de navigation : un en-tete bilingue optionnel (= espace) + ses entrees (= etapes). */
interface NavGroup {
  readonly id: string;
  readonly ar?: string;
  readonly fr?: string;
  readonly items: readonly NavLink[];
}

/** Structure d'un élément de notification. */
interface NotificationItem {
  readonly id: string;
  readonly type: 'warning' | 'info' | 'success' | 'danger';
  /** Code de famille (regroupement), servi par le backend. */
  readonly famille: string;
  readonly titleAr: string;
  readonly titleFr: string;
  readonly descAr: string;
  readonly descFr: string;
  readonly timeAr: string;
  readonly timeFr: string;
  readonly link?: string | null;
  unread: boolean;
}

/** Un groupe du panneau : une famille, ses cartes, son compte de non-lues. */
interface NotificationGroupe {
  readonly def: NotificationFamilleDef;
  readonly items: readonly NotificationItem[];
  readonly unread: number;
}

/** Mode d'affichage du panneau : par famille (repliable) ou en fil chronologique. */
type NotifGroupMode = 'famille' | 'date';
const NOTIF_GROUP_MODE_KEY = 'cce.notifications.groupMode';

/**
 * Les trois états du menu latéral en desktop.
 *
 * <ul>
 *   <li>{@code expanded} — icônes + libellés (248 px), l'état de travail par défaut ;</li>
 *   <li>{@code rail} — icônes seules (72 px), libellés en infobulle : on garde l'accès direct à
 *       toutes les entrées en rendant ~180 px au contenu, ce qui compte sur les écrans de saisie ;</li>
 *   <li>{@code hidden} — plus de menu du tout, pour lire un rapport ou un tableau large.</li>
 * </ul>
 *
 * <p>Sous 900 px ces états ne s'appliquent pas : le menu devient un tiroir modal (voir
 * {@link MainLayout#drawerOpen}).
 */
export type SidebarMode = 'expanded' | 'rail' | 'hidden';
const SIDEBAR_MODE_KEY = 'cce.sidebar.mode';

/** Sous cette largeur, le menu est un tiroir modal ; au-dessus, une colonne de la grille. */
const RUPTURE_COMPACT = '(max-width: 900px)';
/**
 * Entre 900 px et cette borne (tablette paysage, petit portable), le rail est le défaut : la
 * colonne de 248 px y prend le quart de l'écran. Ce n'est qu'un DÉFAUT — un choix explicite de
 * l'utilisateur le remplace et n'est jamais écrasé par un redimensionnement.
 */
const RUPTURE_INTERMEDIAIRE = '(min-width: 900.02px) and (max-width: 1279.98px)';

/**
 * Shell applicatif : topbar (64px) + sidebar (248px, cote inline-start =>
 * a droite en RTL) + zone de contenu avec <router-outlet/>.
 *
 * La sidebar est CONTEXTUELLE : dans l'espace Contrôleur (`/controleur/*`) elle reflete les DEUX
 * ESPACES (docs/spaces/03-controleur-espace-de-travail.md §4) — Contrôle permanent (≈80%, dominant)
 * et Contrôle a posteriori — navigués par OBJETS (worklists + artefacts), PAS par phase. Les 3 phases
 * Planification → Conduite → Reporting sont réalisées IN-PAGE (stepper de séance / indicateur de
 * mission), pas comme liens de menu — sinon doublon « إنجاز المهمة » + entrée Conduite orpheline.
 * Ailleurs : la liste des 8 espaces.
 *
 * Le selecteur de langue bascule la direction du document via I18nService.
 * Sur mobile, la sidebar devient un drawer ouvert par le bouton « menu ».
 */
@Component({
  selector: 'app-main-layout',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, LucideDynamicIcon, CdkTrapFocus, CceTooltip, SlideOver, RechercheGlobale],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.css',
})
export class MainLayout {
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);
  private readonly oidc = inject(OidcSecurityService);
  private readonly notifApi = inject(NotificationApi);
  private readonly actionCountsApi = inject(ActionCountsApi);
  private readonly roles = inject(RoleService);
  private readonly destroyRef = inject(DestroyRef);

  /** Cadence de rafraîchissement du centre de notifications (ms). */
  private static readonly NOTIF_POLL_MS = 60_000;

  constructor() {
    this.loadNotifications();
    this.loadActionCounts();
    // Le flyover ne se chargeait qu'au démarrage (état périmé) → polling léger ; le serveur fait foi
    // pour l'état « lu ». Un canal temps réel (SSE) pourra remplacer le polling plus tard, sans Redis.
    // La pastille de messagerie vit désormais dans la barre du haut (et non plus dans le menu) :
    // elle doit se rafraîchir même sans changement de route, d'où les compteurs dans le même timer.
    const handle = setInterval(() => {
      this.loadNotifications();
      this.loadActionCounts();
    }, MainLayout.NOTIF_POLL_MS);
    this.destroyRef.onDestroy(() => clearInterval(handle));
    // Compteurs de files : rechargés à chaque fin de navigation (donc après chaque action qui
    // change de route) — un dossier tranché disparaît de sa file, la pastille se met à jour.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadActionCounts());

    this.observerLargeur();
    this.gererFocusEtDefilement();
  }

  /**
   * Branche les deux points de rupture sur des signaux.
   *
   * <p>Pourquoi en TypeScript et pas seulement en CSS : le hamburger n'a pas le même SENS de part et
   * d'autre de 900 px — tiroir modal en dessous, masquage de colonne au-dessus. Une media query ne
   * peut pas porter cette différence, seul le composant le peut.
   */
  private observerLargeur(): void {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const brancher = (requete: string, cible: { set(v: boolean): void }) => {
      const mq = window.matchMedia(requete);
      cible.set(mq.matches);
      const surChangement = (e: MediaQueryListEvent) => cible.set(e.matches);
      mq.addEventListener('change', surChangement);
      this.destroyRef.onDestroy(() => mq.removeEventListener('change', surChangement));
    };
    brancher(RUPTURE_COMPACT, this.compact);
    brancher(RUPTURE_INTERMEDIAIRE, this.intermediaire);
  }

  /**
   * Les trois devoirs d'un panneau modal, que le tiroir ne remplissait pas : le focus ENTRE en
   * s'ouvrant, il REVIENT à son bouton en se fermant, et le fond NE DÉFILE PAS derrière le voile.
   *
   * <p>Le retour du focus est explicite et non délégué à {@code cdkTrapFocusAutoCapture} : celui-ci
   * ne restitue qu'à la destruction de la directive, jamais quand on la désactive — or ici le
   * tiroir n'est pas détruit, il se referme.
   */
  private gererFocusEtDefilement(): void {
    if (typeof document === 'undefined') return;
    let premierPassage = true;
    effect(() => {
      const ouvert = this.drawerOpen() && this.compact();
      document.body.classList.toggle('a-tiroir-ouvert', ouvert);
      if (premierPassage) {
        premierPassage = false;
        return;
      }
      if (ouvert) {
        this.sidebarEl?.nativeElement.querySelector<HTMLElement>('a, button')?.focus();
      } else {
        this.menuBtnEl?.nativeElement.focus();
      }
    });
    // Le passage en desktop doit refermer le tiroir : sinon la classe `.open` survit et le menu
    // reste collé par-dessus le contenu au redimensionnement.
    effect(() => {
      if (!this.compact() && this.drawerOpen()) this.drawerOpen.set(false);
    });
    this.destroyRef.onDestroy(() => document.body.classList.remove('a-tiroir-ouvert'));
  }

  /** Le hamburger : on lui REND le focus à la fermeture du tiroir (sinon il retombe sur <body>). */
  @ViewChild('menuBtn') menuBtnEl?: ElementRef<HTMLButtonElement>;
  /** Le menu lui-même : cible du focus initial à l'ouverture du tiroir. */
  @ViewChild('sidebarEl') sidebarEl?: ElementRef<HTMLElement>;
  /** Le panneau des avis : on le referme quand un avis emmène ailleurs (retour PO du 2026-09-21). */
  @ViewChild('notificationsDrawer') notificationsDrawer?: SlideOver;

  /** Langue active (signal) — pilote l'affichage AR/FR. */
  readonly lang = this.i18n.lang;
  /** Le serveur refuse ce compte, suspendu dans le SI (2026-10-20) : tout l'écran le dit. */
  readonly compteSuspendu = compteSuspendu;

  /** Les notifications du panneau (les plus récentes, doublons multi-rôles déjà fusionnés par le serveur). */
  readonly notifications = signal<NotificationItem[]>([]);

  /**
   * Non-lues : LE CHIFFRE DU SERVEUR, tous filtres levés. Il se comptait ici sur les 50 lignes reçues :
   * au-delà, le badge mentait et ne redescendait jamais (revue du 2026-09-16).
   */
  readonly unreadCount = signal(0);

  /** Le dernier chargement a échoué : le panneau le dit, au lieu d'un « rien à signaler » trompeur. */
  readonly notifError = signal(false);

  /**
   * Regroupement (demande du CCE, 2026-09-16) : par FAMILLE métier, repliable, ou fil par date. Le choix
   * est une commodité de poste (localStorage), pas une donnée : sa lecture peut échouer sans conséquence.
   */
  readonly notifGroupMode = signal<NotifGroupMode>(lireModeGroupement());

  /** Familles repliées dans le panneau (par code). */
  readonly notifRepliees = signal<ReadonlySet<string>>(new Set());

  /**
   * Les groupes à afficher. En mode « date », un seul groupe sans en-tête : la carte se rend au même
   * endroit dans les deux modes, il n'y a qu'un gabarit à tenir.
   */
  readonly notificationGroups = computed<readonly NotificationGroupe[]>(() => {
    const items = this.notifications();
    if (this.notifGroupMode() === 'date') {
      return items.length
        ? [{ def: familleDe('AUTRE'), items, unread: items.filter((n) => n.unread).length }]
        : [];
    }
    return NOTIFICATION_FAMILLES.map((def) => {
      const siens = items.filter((n) => familleDe(n.famille).code === def.code);
      return { def, items: siens, unread: siens.filter((n) => n.unread).length };
    }).filter((g) => g.items.length > 0);
  });

  toggleNotifGroupMode(): void {
    const mode: NotifGroupMode = this.notifGroupMode() === 'famille' ? 'date' : 'famille';
    this.notifGroupMode.set(mode);
    try {
      localStorage.setItem(NOTIF_GROUP_MODE_KEY, mode);
    } catch {
      // Stockage indisponible (navigation privée, quota) : le choix vaut pour la session.
    }
  }

  toggleNotifGroupe(code: string): void {
    this.notifRepliees.update((s) => {
      const n = new Set(s);
      if (n.has(code)) n.delete(code); else n.add(code);
      return n;
    });
  }

  estRepliee(code: string): boolean {
    return this.notifRepliees().has(code);
  }

  // ————— Messagerie فضاء التواصل : icône de la barre du haut (demande du 2026-08-06) —————
  //
  // L'entrée de menu latéral a été RETIRÉE : la messagerie n'est pas une rubrique de travail parmi
  // d'autres, c'est un canal — sa place est à côté de la cloche, où l'on regarde ce qui arrive.

  /**
   * La messagerie n'existe que dans DEUX espaces (Président ↔ contrôleurs) : pour les autres rôles,
   * l'icône n'a pas lieu d'être. On l'affiche donc selon le rôle, et non selon l'URL courante — un
   * président qui consulte une page de l'espace contrôleur garde son propre accès.
   */
  readonly showMessagerie = computed(() => this.roles.isPresident() || this.roles.isControleur());

  /** Où mène l'icône : chaque rôle vers SON écran (le composant est le même, la route diffère). */
  readonly messagerieRoute = computed(() =>
    this.roles.isPresident() ? '/presidence/communication' : '/controleur/communication',
  );

  /**
   * Messages non lus, servis par le même endpoint que les pastilles de menu (`action-counts`) : le
   * serveur fait foi. Les deux clés cohabitent car un compte peut porter les deux rôles.
   */
  readonly messagesNonLus = computed(() => {
    const c = this.actionCounts();
    return (c['president.communication'] ?? 0) + (c['controleur.communication'] ?? 0);
  });

  /** Texte de la pastille — plafonné, comme celles du menu. */
  readonly messagesBadgeText = computed(() => {
    const n = this.messagesNonLus();
    return n > 9 ? '9+' : String(n);
  });

  private loadNotifications(): void {
    this.notifApi
      .page({ size: 30 })
      .pipe(
        catchError(() => of(null as NotificationPage | null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((page) => {
        if (!page) {
          // Un échec ne vide pas le panneau (la dernière liste connue reste) : il se signale.
          this.notifError.set(true);
          return;
        }
        this.notifError.set(false);
        // Tolérance à une réponse d'une autre forme (ancien serveur, mock) : jamais d'exception dans le
        // souscripteur — elle laisserait le signal figé et le panneau muet.
        const items = Array.isArray(page.items) ? page.items : [];
        this.notifications.set(items.map((n) => this.toItem(n)));
        this.unreadCount.set(typeof page.unread === 'number' ? page.unread : items.filter((n) => n.unread).length);
      });
  }

  /** Mappe une notification backend (bilingue) vers l'élément d'affichage du flyover. */
  private toItem(n: NotificationView): NotificationItem {
    const type =
      n.type === 'REPORT_RETURNED' ? 'warning'
      : n.type === 'REPORT_VALIDATED' ? 'success'
      : n.type === 'REPORT_REJECTED' ? 'danger'
      : 'info';
    const time = this.relativeTime(n.createdAt);
    return {
      id: n.id, type, famille: n.famille,
      titleAr: n.title?.ar ?? n.title?.fr ?? '', titleFr: n.title?.fr ?? n.title?.ar ?? '',
      descAr: n.body?.ar ?? n.body?.fr ?? '', descFr: n.body?.fr ?? n.body?.ar ?? '',
      timeAr: time, timeFr: time, link: n.link, unread: n.unread,
    };
  }

  private relativeTime(iso: string | null): string {
    if (!iso) {
      return '';
    }
    // La date arrive en ISO-8601 AVEC décalage : `Date` la place dans le fuseau du navigateur.
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    const locale = this.lang() === 'ar' ? 'ar-TN' : 'fr-TN';
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
      .format(d);
  }

  /** Tout marquer lu : le serveur marque TOUT (pas la page), puis on relit son compteur. */
  markAllAsRead(): void {
    this.notifications.update(list => list.map(n => ({ ...n, unread: false })));
    this.unreadCount.set(0);
    this.notifApi
      .markAllRead()
      .pipe(catchError(() => of(void 0)), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadNotifications());
  }

  /** « مسح الكل » : masque côté SERVEUR (par utilisateur) — le panneau ne se remplit plus 60 s après. */
  clearAll(): void {
    this.notifications.set([]);
    this.unreadCount.set(0);
    this.notifApi
      .hideAll()
      .pipe(catchError(() => of(void 0)), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadNotifications());
  }

  /** Referme le panneau des avis — le tiroir partagé ne lève pas s'il est déjà fermé. */
  private fermerPanneauAvis(): void {
    this.notificationsDrawer?.hide();
  }

  /**
   * Ouvre une notification : la marque lue (et ses jumelles multi-rôles), REFERME le panneau, puis
   * navigue vers sa cible.
   *
   * <p>La fermeture manquait (retour PO du 2026-09-21) : le panneau restait par-dessus la page vers
   * laquelle l'avis venait de conduire, et il fallait le chasser à la main pour voir où l'on était
   * arrivé. Elle vaut pour TOUS les avis — un avis qui emmène ailleurs a fini son office.
   */
  openNotification(item: NotificationItem): void {
    if (item.unread) {
      this.notifications.update(list => list.map(n => n.id === item.id ? { ...n, unread: false } : n));
      this.unreadCount.update((c) => Math.max(0, c - 1));
      this.notifApi
        .markRead(item.id)
        .pipe(catchError(() => of(void 0)), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.loadNotifications());
    }
    this.fermerPanneauAvis();
    if (item.link) {
      this.router.navigateByUrl(item.link);
    }
  }

  /** La croix d'une carte : masque pour moi, côté serveur. */
  removeNotification(id: string, event: Event): void {
    event.stopPropagation();
    const etaitNonLue = this.notifications().some((n) => n.id === id && n.unread);
    this.notifications.update(list => list.filter(n => n.id !== id));
    if (etaitNonLue) {
      this.unreadCount.update((c) => Math.max(0, c - 1));
    }
    this.notifApi
      .hide(id)
      .pipe(catchError(() => of(void 0)), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadNotifications());
  }

  /** Recharger après un échec signalé dans le panneau. */
  retryNotifications(): void {
    this.loadNotifications();
  }

  /** État de l'utilisateur connecté via OIDC (Keycloak). */
  readonly userDataResult = toSignal(this.oidc.userData$);
  readonly user = computed(() => this.userDataResult()?.userData);

  /** « الاسم واللقب » de la fiche en arabe, le nom du jeton sinon (cf. NomUtilisateurService). */
  private readonly nomUtilisateur = inject(NomUtilisateurService).nom;

  readonly userName = computed(() => {
    const u = this.user();
    if (!u) return 'Utilisateur CCE';
    return this.nomUtilisateur() || u.preferred_username || 'Utilisateur';
  });

  readonly userEmail = computed(() => {
    const u = this.user();
    return u?.email || '';
  });

  readonly userInitials = computed(() => {
    const u = this.user();
    if (!u) return 'CCE';
    const first = u.given_name?.[0] || '';
    const last = u.family_name?.[0] || '';
    if (first || last) {
      return (first + last).toUpperCase();
    }
    return u.name?.[0]?.toUpperCase() || u.preferred_username?.[0]?.toUpperCase() || 'U';
  });

  readonly uiTexts = computed(() => {
    const isAr = this.lang() === 'ar';
    return {
      profile: isAr ? 'الملف الشخصي' : 'Mon profil',
      logout: isAr ? 'تسجيل الخروج' : 'Se déconnecter',
      // Compte suspendu dans le SI (2026-10-20) — libellés provisoires ; « معطّل » et « الخلية الإدارية » sont à l'écran.
      suspenduTitre: isAr ? 'حسابك معطّل' : 'Votre compte est désactivé',
      suspenduTexte: isAr
        ? 'تمّ تعطيل حسابك في المنظومة، فلم يعد بإمكانك استعمالها. للاستفسار، يُرجى الاتصال بالخلية الإدارية.'
        : 'Votre compte a été désactivé dans le SI : vous ne pouvez plus l’utiliser. Pour toute question, adressez-vous à la Cellule administrative.',
      language: isAr ? 'اللغة' : 'Langue',
      userMenuLabel: isAr ? 'قائمة المستخدم' : 'Menu utilisateur',
      // ⚠️ Hors glossaire, À RATIFIER. `فضاء` n'est pas inventé : c'est le mot du CCE lui-même, dans
      // l'intitulé de ses propres notes (« ملاحظات بخصوص فضاء المراقب », 2026-08-06).
      spacesTitle: isAr ? 'الفضاءات' : 'Espaces',
      spaceSwitcherLabel: isAr ? 'تغيير الفضاء' : "Changer d'espace",
      currentSpaceLabel: isAr ? 'الفضاء الحالي' : 'Espace courant',
      notifications: isAr ? 'الإشعارات' : 'Notifications',
      notificationsEmpty: isAr ? 'لا توجد إشعارات جديدة' : 'Aucune nouvelle notification',
      notificationsEmptyDesc: isAr ? 'أنت على اطلاع بكل المستجدات! كل الإشعارات المقروءة أو المعالجة ستظهر هنا.' : 'Vous êtes à jour ! Toutes les notifications lues ou traitées s\'afficheront ici.',
      markAllAsRead: isAr ? 'تحديد الكل كمقروء' : 'Tout marquer comme lu',
      clearAll: isAr ? 'مسح الكل' : 'Effacer tout',
      // Regroupement (CCE 2026-09-16). ⚠️ « حسب النوع » / « حسب التاريخ » hors glossaire, à ratifier.
      groupByFamille: isAr ? 'حسب النوع' : 'Par type',
      groupByDate: isAr ? 'حسب التاريخ' : 'Par date',
      groupModeLabel: isAr ? 'طريقة العرض' : 'Mode d’affichage',
      notificationsError: isAr ? 'تعذّر تحميل الإشعارات.' : 'Les notifications n’ont pas pu être chargées.',
      retry: isAr ? 'إعادة المحاولة' : 'Réessayer',
      unreadShort: isAr ? 'غير مقروء' : 'non lu',
      noUnread: isAr ? 'لا توجد إشعارات غير مقروءة' : 'Aucune notification non lue',
      allCaughtUp: isAr ? 'كل شيء جاهز!' : 'Vous êtes à jour !',
      viewAll: isAr ? 'عرض كل الإشعارات' : 'Voir toutes les notifications',
      notificationLabel: isAr ? 'قائمة الإشعارات' : 'Menu des notifications',
      // ⚠️ « فضاء التواصل » hors glossaire (W-07) — verbatim du PV du 24/07, à ratifier.
      messagerieLabel: isAr ? 'فضاء التواصل' : 'Communication',
      messagerieNonLus: isAr ? 'رسائل غير مقروءة' : 'messages non lus',
      // L'invite dit ce que l'espace cherche vraiment (TRV-10) : dans l'espace CAEEP, ni contrôleur ni
      // mission, mais des cabinets, des dossiers, des experts et des EEP. ⚠️ Forme CAEEP à ratifier.
      searchPlaceholder: this.inCaeep()
        ? (isAr ? 'ابحث عن مكتب، ملف، خبير، مؤسسة…' : 'Rechercher un cabinet, un dossier, un expert, une EEP…')
        : (isAr ? 'ابحث عن مؤسسة، مراقب، مهمة، تقرير…' : 'Rechercher une entreprise, un contrôleur, une mission…'),
      // Commandes du menu latéral. ⚠️ Chrome d'interface, HORS glossaire : aucun de ces mots n'est un
      // terme métier, ils décrivent un geste d'affichage. À relire au passage FR de fin de projet.
      navAfficher: isAr ? 'إظهار القائمة الجانبية' : 'Afficher le menu',
      navMasquer: isAr ? 'إخفاء القائمة الجانبية' : 'Masquer le menu',
      navReduire: isAr ? 'تصغير القائمة' : 'Réduire le menu',
      navDeployer: isAr ? 'توسيع القائمة' : 'Déployer le menu',
    };
  });

  /** Libellé du hamburger : il dit CE QU'IL VA FAIRE, pas l'état courant. */
  readonly navToggleLabel = computed(() => {
    const t = this.uiTexts();
    if (this.compact()) return this.drawerOpen() ? t.navMasquer : t.navAfficher;
    return this.sidebarMode() === 'hidden' ? t.navAfficher : t.navMasquer;
  });

  /** Idem pour le chevron du pied. */
  readonly railToggleLabel = computed(() =>
    this.sidebarMode() === 'rail' ? this.uiTexts().navDeployer : this.uiTexts().navReduire,
  );

  /**
   * Déconnexion — <b>de l'application ET de Keycloak</b>.
   *
   * <h2>Le défaut corrigé</h2>
   * {@code logoff()} seul laissait la session survivre : en se reconnectant, on retrouvait le compte
   * précédent, dans son espace, sans qu'aucun formulaire ne soit présenté. La cause n'est pas le SI mais le
   * <b>cookie SSO de Keycloak</b> : s'il survit, la « nouvelle » connexion réutilise silencieusement
   * l'ancienne session — on croit s'authentifier autrement, on ne nous le demande jamais.
   *
   * <p>Dans {@code angular-auth-oidc-client} 21, deux branches de {@code logoff} sortent en journalisant
   * « logoff was only locally » <b>sans rien effacer</b> — {@code resetAuthorizationData} n'est appelé que
   * dans la branche {@code urlHandler}. C'est donc celle-ci qu'on emprunte : elle garantit l'effacement
   * local, et nous laisse conduire la redirection vers la fin de session.
   *
   * <p>Trois filets, parce qu'une session qui survit à une déconnexion est un défaut de sécurité, pas une
   * gêne : révocation des jetons au serveur, effacement local, puis fin de session Keycloak. Si l'une des
   * étapes échoue, les autres s'appliquent quand même.
   */
  logout(): void {
    // Les éléments récents de la recherche globale ne survivent pas à la session (titres d'objets en clair).
    effacerRecents();
    let redirige = false;
    const partir = (): void => {
      this.oidc.logoffLocal();
      window.location.href = '/';
    };
    this.oidc
      .logoffAndRevokeTokens(undefined, {
        urlHandler: (url: string) => {
          redirige = true;
          window.location.href = url;
        },
      })
      .subscribe({
        // Aucune URL de fin de session produite : la bibliothèque n'a rien effacé, on s'en charge.
        next: () => {
          if (!redirige) {
            partir();
          }
        },
        error: partir,
      });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    const activeEl = document.activeElement;
    const isEditable = activeEl && (
      activeEl.tagName === 'INPUT' ||
      activeEl.tagName === 'TEXTAREA' ||
      activeEl.hasAttribute('contenteditable')
    );

    // Frappe dans un champ : aucun raccourci ici. ⌘K et « / » appartiennent à la recherche globale,
    // qui les écoute elle-même (RechercheGlobale.surToucheGlobale).
    if (isEditable) {
      return;
    }

    // ⌘/Ctrl+B — afficher/masquer la navigation (convention VS Code, Slack, Notion).
    // La garde « champ éditable » plus haut protège l'éditeur riche, où ⌘B veut dire GRAS.
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'b') {
      event.preventDefault();
      this.basculerNavigation();
      return;
    }

    // Échap ferme le tiroir : un panneau modal DOIT se fermer au clavier.
    if (event.key === 'Escape' && this.drawerOpen()) {
      event.preventDefault();
      this.closeDrawer();
    }
  }


  // ═══════════════════════ ÉTAT DU MENU LATÉRAL ═══════════════════════
  //
  // DEUX CONTRÔLES, JAMAIS UN CYCLE À TROIS. Un bouton unique qui ferait défiler
  // déployé → rail → masqué est le piège classique : rien ne dit où l'on atterrit. On sépare donc
  // les deux intentions, comme le font VS Code, Teams ou Linear :
  //
  //   • le hamburger de la barre du haut  → AFFICHER / MASQUER  (et, en compact, ouvrir le tiroir) ;
  //   • le chevron du pied du menu        → DÉPLOYÉ ⇄ RAIL      (absent en compact).
  //
  // Le raccourci ⌘/Ctrl+B double le hamburger (convention VS Code / Slack).

  /**
   * Choix EXPLICITE de l'utilisateur, mémorisé sur le poste. {@code null} = automatique : c'est
   * alors la largeur de l'écran qui décide. Dès le premier clic, le choix prime et un
   * redimensionnement ne le défait plus — sans quoi brancher un écran externe reconfigurerait le
   * menu dans le dos de l'utilisateur.
   */
  private readonly modeChoisi = signal<SidebarMode | null>(lireModeSidebar());

  /** Le dernier état VISIBLE, pour que « réafficher » rende le menu tel qu'il était avant. */
  private dernierModeVisible: Exclude<SidebarMode, 'hidden'> = 'expanded';

  /** Le viewport est compact (< 900 px) : le menu est un tiroir modal, pas une colonne. */
  readonly compact = signal(false);

  /** Viewport intermédiaire : le rail est le défaut tant que l'utilisateur n'a rien choisi. */
  private readonly intermediaire = signal(false);

  /** L'état effectif du menu en desktop (choix explicite, sinon défaut lié à la largeur). */
  readonly sidebarMode = computed<SidebarMode>(
    () => this.modeChoisi() ?? (this.intermediaire() ? 'rail' : 'expanded'),
  );

  /** Le menu est réduit à ses icônes : pilote les infobulles ET le masquage des libellés. */
  readonly estEnRail = computed(() => !this.compact() && this.sidebarMode() === 'rail');

  /** Etat d'ouverture du tiroir (compact uniquement). */
  readonly drawerOpen = signal(false);

  /**
   * Le menu est hors d'atteinte : masqué en desktop, ou tiroir fermé en compact.
   *
   * <p>Pilote {@code inert}. Sans lui, le menu fermé RESTE tabulable : on quittait la barre du haut
   * pour traverser dix-sept liens invisibles, hors écran, avant d'atteindre le contenu.
   */
  readonly navInerte = computed(() =>
    this.compact() ? !this.drawerOpen() : this.sidebarMode() === 'hidden',
  );

  /** URL courante (signal), reactualisee a chaque navigation. */
  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /** Les 8 espaces metier du SI CCE (placeholders Phase 0). */
  readonly spaces: readonly NavLink[] = [
    { id: 'presidence', ar: 'رئاسة الهيئة', fr: 'Présidence du CCE', route: '/presidence/dashboard', icon: 'crown' },
    { id: 'conseil', ar: 'أعضاء مجلس الهيئة', fr: 'Membres du Conseil', route: '/conseil/dashboard', icon: 'users' },
    { id: 'controleurs', ar: 'مراقبو الدولة', fr: "Contrôleurs d'État", route: '/controleur/dashboard', icon: 'user-check' },
    { id: 'cellule-admin', ar: 'الخلية الإدارية', fr: 'Cellule Administrative', route: '/cellule/dashboard', icon: 'building' },
    { id: 'sec-caeep', ar: 'كتابة لجنة التدقيق في حسابات المؤسسات والمنشآت العمومية', fr: 'Secrétariat de la CAEEP', icon: 'file-check' },
    { id: 'reporting', ar: 'خلية إعداد التقارير', fr: 'Cellule de Reporting', route: '/reporting/dashboard', icon: 'bar-chart-3' },
    // 9ᵉ espace (TR-03, CR 2026-07-24). L'arabe est VERBATIM du référentiel du CCE du 03/06/2026
    // (« إطار مرجعي لمراقبة جودة العمل الرقابي ») — il n'est pas inventé. Le libellé français des
    // deux organes qui l'occupent, lui, n'est pas ratifié.
    { id: 'qualite', ar: 'مراقبة جودة العمل الرقابي', fr: 'Contrôle de la qualité du travail des contrôleurs', route: '/qualite/campagnes', icon: 'list-checks' },
    { id: 'admin-fonctionnel', ar: 'المسؤول الوظيفي', fr: 'Administrateur Fonctionnel', icon: 'settings' },
    { id: 'admin-technique', ar: 'المسؤول الفني', fr: 'Administrateur Technique', icon: 'wrench' },
  ];

  /**
   * Menu de l'espace Contrôleur d'État — DEUX ESPACES navigués par OBJETS (docs/spaces/
   * 03-controleur-espace-de-travail.md §4/§6/§7/§13). Les 3 phases (Planification → Conduite →
   * Reporting) NE sont PAS des liens de menu : elles sont réalisées IN-PAGE (stepper de séance,
   * indicateur de mission). La sidebar ne liste donc que des worklists / artefacts distincts :
   *  • Permanent : برنامج العمل الأسبوعي (séances) · تقرير مراقب الدولة (rapport ANNUEL, artefact agrégé).
   *  • A posteriori : الإسناد والإشعار بالاستلام (inbox d'affectations à accuser) · مهماتي (mes missions ;
   *    la Conduite et le تقرير المهمة vivent DANS la mission). → plus de « إنجاز المهمة » en double.
   * Implémentés : Tableau de bord, Calendrier, Programme hebdomadaire, Mes missions ; le reste = placeholders.
   *
   * WORDING (Regle n°1) — libelles AR ACTES au glossaire (docs/01-glossaire.md §4bis, validés 2026-06-07).
   * NE PAS modifier un libelle ici sans mettre a jour le glossaire (et inversement).
   *
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * ARBORESCENCE ARRÊTÉE PAR LE CCE — retours du 2026-08-27 (« فضاء مراقب الدولة1.docx », lot A du
   * plan `docs/workshops/plan-retours-cce-2026-08-27.md`). Le document donne la liste ORDONNÉE des
   * treize entrées et de leurs deux groupes ; elle est reprise telle quelle.
   *
   * ⚠️ UNE QUATORZIÈME entrée s'y est AJOUTÉE le 2026-08-27 : `المهمات المنجزة` (lot G). Elle ne vient
   * pas de ce document-ci mais de l'AUTRE (`espace controleur.docx`), qui ouvre sur deux rubriques —
   * les missions achevées et les missions en cours. Les treize entrées arrêtées ici gardent leur
   * ordre : la nouvelle se range en fin du groupe a posteriori (voir la note à son emplacement).
   *
   * Ce qui change, et pourquoi le CCE le demande :
   *  • Les groupes `قاعدة بيانات` et `عام` DISPARAISSENT — pas leurs écrans, qui se redistribuent.
   *    Le contrôleur ne cherchait pas une « base de données » : il cherchait des notes d'alerte et
   *    des fiches d'EEP, qui relèvent l'une et l'autre du contrôle PERMANENT.
   *  • `بطاقة بيانات مراقب الدولة` n'a plus d'entrée propre : elle s'atteint depuis `التعيين والتكليف`,
   *    l'écran qui dit qui l'on est et ce qu'on nous a confié. La route `/controleur/profil` VIT
   *    toujours — c'est le chemin d'accès qui change, pas l'écran.
   *  • `طلبات المساندة` remonte au PREMIER NIVEAU, en fin de menu : la demande d'appui ne se
   *    rattache à aucune des deux natures de contrôle, elle les traverse.
   *
   * ⚠️ Orthographe : le document écrit `الاعلام` et `انجاز` sans hamza — raccourci de frappe, non
   * choix orthographique. Les libellés d'interface la portent (`الإعلام`, `إنجاز`).
   * ✅ `مذكرات الإعلام` (lot F2, fait) : le document de MENU, écrit à 06:12, disait encore
   * `بطاقات الإعلام` ; le glossaire relu dix-neuf minutes plus tard tranche `Note d'Alerte` en
   * `مذكرة إعلام`. C'est le glossaire qui fait foi — règle n°1 — et l'entrée porte donc la forme
   * qu'il retient, non celle que le document de menu avait sous les yeux.
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   */
  readonly controleurGroups: readonly NavGroup[] = [
    {
      // ARBORESCENCE DEMANDÉE PAR LE CCE (notes du 2026-08-06, § « وسائل العمل » ; ordre confirmé
      // le 2026-08-27). Les entrées transverses d'abord, puis les deux natures de contrôle.
      // `التعيين والتكليف` reste AU-DESSUS de `برنامج العمل` : on est désigné avant de programmer.
      id: 'c-accueil',
      items: [
        { id: 'c-dashboard', ar: 'لوحة القيادة', fr: 'Tableau de bord', route: '/controleur/dashboard', icon: 'layout-dashboard' },
        // Remonté du groupe « عام » : le client le place en 3ᵉ position, avant même l'affectation.
        // [AR provisoire — « Espace documentaire » non ratifié au glossaire ; à valider (CR conception §8).]
        { id: 'c-espace-doc', ar: 'فضاء الوثائق والمعارف', fr: 'Espace documentaire', route: '/controleur/espace-documentaire', icon: 'book-open' },
        // Remonté du groupe « a posteriori » : l'écran couvre les missions PERMANENTES (مقرر تعيين) ET
        // a posteriori (إذن بمأمورية), il n'appartenait donc à aucun des deux groupes.
        // Depuis le 2026-08-27, il porte aussi la fiche du contrôleur (بطاقة بيانات مراقب الدولة).
        { id: 'c-post-plan', ar: 'التعيينات', fr: 'Désignation & ordre de mission', route: '/controleur/affectations', icon: 'inbox', badgeKey: 'controleur.affectations' },
        // CT-01 (CR 2026-07-24) : `الرزنامة` et `برنامج العمل الأسبوعي` fusionnés en UNE entrée.
        // ⚠ Wording — ÉCART AU GLOSSAIRE, demandé par le CCE (2026-07-29), à faire ratifier. Le glossaire
        // (§ pièges tranchés) impose `برنامج العمل الأسبوعي` et met en garde contre la confusion avec
        // `برنامج العمل السنوي` (le PTA, autre objet). Le risque est signalé, la décision est celle du CCE.
        { id: 'c-calendar', ar: 'برنامج العمل', fr: 'Programme de travail', route: '/controleur/programme-hebdo', icon: 'calendar' },
      ],
    },
    {
      id: 'c-permanent',
      ar: 'مهمات الرقابة الدائمة',
      fr: 'Contrôle permanent',
      items: [
        // `إنجاز المهام الرقابية` AU PLURIEL (CCE, 2026-08-27) — et c'est ce pluriel qui LÈVE ENFIN
        // l'homonymie avec l'entrée a posteriori. Le glossaire §4bis la signalait depuis août comme
        // un écart assumé (« le geste porte ce nom, le groupe dit sur quoi il s'exerce ») ; le CCE
        // la lève de lui-même, sans qu'on ait eu à la faire arbitrer.
        { id: 'c-perm-execution', ar: 'إنجاز المهام الرقابية', fr: 'Exécution des missions de contrôle', route: '/controleur/execution-mission', icon: 'file-check' },
        // Descendue du groupe `قاعدة بيانات` : la مذكرة إعلام naît du contrôle permanent, elle
        // n'était rangée « base de données » que par défaut d'un meilleur endroit.
        { id: 'c-note-alerte', ar: 'مذكرات الإعلام', fr: "Notes d'alerte", route: '/controleur/notes-alerte', icon: 'bell-ring', badgeKey: 'controleur.notesAlerte' },
        // ORDRE INVERSÉ (CCE, 2026-08-27) : le trimestriel AVANT l'annuel. Il se rend quatre fois
        // par an, l'annuel une seule — l'ordre du menu suit désormais celui du travail.
        { id: 'c-perm-rapport-trim', ar: 'تقرير النشاط الثلاثي', fr: 'Rapport trimestriel', route: '/controleur/rapport-trimestriel', icon: 'calendar-days', badgeKey: 'controleur.rapportTrimestriel' },
        // Rapport ANNUEL du contrôleur = artefact agrégé distinct (Écran 7).
        { id: 'c-perm-rapport', ar: 'التقرير السنوي', fr: 'Rapport annuel', route: '/controleur/rapport-annuel', icon: 'file-spreadsheet', badgeKey: 'controleur.rapportAnnuel' },
        // Descendue du groupe `قاعدة بيانات` et RACCOURCIE : le libellé long
        // (`بطاقات بيانات المؤسسات والمنشآت العمومية`) avait été allongé sur demande du client le
        // 2026-08-06 ; il revient en arrière le 2026-08-27.
        { id: 'c-entreprises', ar: 'بيانات المؤسسات والمنشآت العمومية', fr: 'Fiches des EEP', route: '/controleur/entreprises', icon: 'building-2' },
      ],
    },
    {
      // ══ UNE SEULE ENTRÉE (CCE, atelier du 2026-08-28) ══
      // Ce groupe portait QUATRE entrées — إنجاز المهمة, التقرير الأولي, التقرير النهائي,
      // المهمات المنجزة — arrêtées par le CCE le 2026-08-27. Il en veut désormais UNE, qui ouvre une
      // page de cartes, sur le modèle de l'espace de la خلية إدارية.
      //
      // ⚠️ AUCUN ÉCRAN N'EST SUPPRIMÉ, et c'est ce qu'il faut vérifier : les quatre destinations
      // existent toujours et restent atteignables par leur route. Ce lot déplace un chemin d'accès,
      // il ne retire pas une capacité — la confusion entre les deux serait la façon la plus simple
      // de perdre un écran sans que personne ne le remarque.
      //
      // Le groupe avait perdu son TITRE le 28/08 (l'en-tête et l'entrée unique auraient dit deux fois
      // la même chose). ⚠️ EFFET NON VOULU, vu par le CCE en staging (2026-09-18) : un groupe sans
      // en-tête se FOND dans le groupe qui le précède, et l'a posteriori paraissait rangé sous
      // « مهمات الرقابة الدائمة ». Le CCE demande la section explicite, au libellé de son document de
      // menu (« مهمات الرقابة اللاحقة »). Le titre revient ; l'entrée et sa page ne changent pas.
      id: 'c-posteriori',
      ar: 'مهمات الرقابة اللاحقة',
      fr: 'Contrôle a posteriori',
      items: [
        { id: 'c-posteriori-accueil', ar: 'الرقابة اللاحقة أو الظرفية', fr: 'Contrôle a posteriori / circonstanciel', route: '/controleur/posteriori', icon: 'briefcase' },
      ],
    },
    {
      // ═════════════════════════════════════════════════════════════════════════════════════════
      // DERNIÈRE ENTRÉE, SANS GROUPE (CCE, 2026-08-27). Le document la place seule après les deux
      // natures de contrôle, et c'est juste : une demande d'appui ne relève ni du permanent ni de
      // l'a posteriori — elle les traverse. Un groupe d'un seul élément aurait menti sur ce point.
      // Sans titre, elle est SÉPARÉE du groupe précédent par un filet (`nav-group-sep`, 2026-09-18) :
      // sinon elle paraîtrait appartenir à « مهمات الرقابة اللاحقة » — le défaut même que le CCE a relevé.
      //
      // Les groupes `قاعدة بيانات` et `عام` sont RETIRÉS ici. Aucun écran n'est supprimé :
      //  • `مذكرات الإعلام` et les fiches d'EEP sont passées au groupe PERMANENT ;
      //  • `بطاقة بيانات مراقب الدولة` s'atteint désormais depuis `التعيين والتكليف` (l'écran garde
      //    sa route `/controleur/profil` et son entrée dans le menu du compte) ;
      //  • `طلبات المساندة` est ici.
      //
      // Rappel de deux retraits ANTÉRIEURS, pour que personne ne les réintroduise en lisant ce
      // fichier : `التوصيات` (atelier du 2026-08-07 — les tables `mission.recommendation` et
      // `recommendation_followup` sont CONSERVÉES, 61 + 3 lignes ; ⚠️ le CCE les redemande dans le
      // rapport FINAL, cf. lot E du plan du 2026-08-27) et `الإشعارات` (même atelier — l'entrée
      // n'avait pas de route, les notifications ont leur cloche dans la barre du haut).
      // ═════════════════════════════════════════════════════════════════════════════════════════
      id: 'c-appui-groupe',
      items: [
        { id: 'c-appui', ar: 'طلبات المساندة', fr: "Demandes d'appui", route: '/controleur/demandes-appui', icon: 'help-circle' },
      ],
    },
  ];

  /**
   * Menu de l'espace Présidence (رئاسة الهيئة) — suivi cross-contrôleur + décision sur les notes
   * d'alerte. Le pilotage des demandes d'appui est affiché sur le dashboard (placeholder de menu).
   * WORDING (Règle n°1) : libellés AR du glossaire (رئاسة الهيئة, مذكرات الإعلام, طلبات المساندة).
   */
  readonly presidenceGroups: readonly NavGroup[] = [
    {
      id: 'p-accueil',
      items: [
        { id: 'p-dashboard', ar: 'لوحة القيادة', fr: 'Tableau de bord', route: '/presidence/dashboard', icon: 'layout-dashboard' },
        // [AR provisoire — « Espace documentaire » non ratifié au glossaire ; à valider (CR conception §8).]
        { id: 'p-espace-doc', ar: 'فضاء الوثائق والمعارف', fr: 'Espace documentaire', route: '/presidence/espace-documentaire', icon: 'book-open' },
        { id: 'p-perm-plan', ar: 'برنامج العمل الأسبوعي', fr: 'Programme hebdomadaire', route: '/presidence/programme-hebdo', icon: 'calendar-days' },
      ],
    },
    {
      id: 'p-suivi',
      ar: 'المتابعة',
      fr: 'Suivi',
      items: [
        { id: 'p-entreprises', ar: 'المؤسسات والمنشآت العمومية', fr: 'Établissements et entreprises publics', route: '/presidence/entreprises', icon: 'building-2' },
        // « قاعدة بيانات المؤسسات » (document CCE du 2026-09-04) : la fiche consolidée d'une EEP par exercice —
        // libellé VERBATIM du titre du document, même page dans les espaces Reporting et Cellule administrative.
        { id: 'p-base-donnees', ar: 'قاعدة بيانات المؤسسات', fr: 'Base de données des EEP', route: '/presidence/base-donnees', icon: 'database' },
        { id: 'p-programme', ar: 'برنامج الرقابة السنوي', fr: 'Programme annuel de contrôle', route: '/presidence/programme', icon: 'calendar-days' },
        { id: 'p-missions', ar: 'مهام الرقابة اللاحقة', fr: 'Missions a posteriori', route: '/presidence/missions', icon: 'briefcase' },
        // « التواصل مع مراقبي الدولة » (2026-10-01) : remplace les entrées « مذكرات الإعلام » et « طلبات المساندة » —
        // avec la messagerie, ce sont les trois onglets d'une même rubrique. [AR PROVISOIRE — à ratifier CCE.]
        {
          id: 'p-echanges', ar: 'التواصل مع مراقبي الدولة', fr: 'Échanges avec les contrôleurs',
          route: '/presidence/demandes-appui', actifSur: ['/presidence/notes-alerte', '/presidence/communication'],
          icon: 'messages-square', badgeKeys: ['president.notesAlerte', 'president.communication'],
        },
        { id: 'p-rapports', ar: 'تقارير في انتظار المصادقة', fr: 'Rapports à valider', route: '/presidence/rapports', icon: 'file-check', badgeKey: 'president.rapports' },
        // « التقارير الخصوصية » de la Cellule de Reporting, en lecture (CCE, 2026-10-20).
        { id: 'p-rapports-specifiques', ar: 'التقارير الخصوصية', fr: 'Rapports spécifiques', route: '/presidence/rapports-specifiques', icon: 'file-archive' },
        // PR-05 : l'écran suit désormais TOUTES les passations en cours, pas seulement celles à viser —
        // le libellé le dit. La pastille, elle, reste sur VISA_PENDING : elle signale une action attendue.
        { id: 'p-passations', ar: 'متابعة نقل المهام', fr: 'Suivi des passations', route: '/presidence/passations', icon: 'arrow-left-right', badgeKey: 'president.passations' },
        // تعويض مؤقت (CCE, appel du 27/10/2026) : décidés par le seul Président, ils n'étaient suivis nulle part. La pastille
        // compte les remplacements en conflit (deux remplaçants sur la même EEP et la même période). [AR PROVISOIRE.]
        { id: 'p-remplacements', ar: 'متابعة التعويض المؤقت', fr: 'Suivi des remplacements', route: '/presidence/remplacements', icon: 'refresh-cw', badgeKey: 'president.remplacements' },
        // Le Président PILOTE le dispositif qualité (décision R1 : les deux organes lui sont rattachés)
        // et signe la conclusion annuelle. L'écran est monté sous son préfixe pour qu'il ne quitte pas
        // son espace. [AR VERBATIM du référentiel du CCE du 03/06/2026 ; le FR n'est pas ratifié.]
        { id: 'p-qualite', ar: 'مراقبة جودة العمل الرقابي', fr: 'Contrôle de la qualité du travail des contrôleurs', route: '/presidence/qualite/campagnes', icon: 'list-checks' },
        // Le Président SIGNE l'acte de nomination du CAC, et c'est cette signature qui crée le mandat
        // (processus 05, phase 6). Sans cette entrée il détiendrait `cac-nomination:nommer` sans chemin
        // pour l'exercer.
        { id: 'p-cac-nom', ar: 'تعيين مراقبي الحسابات', fr: 'Nomination des CAC', route: '/presidence/nominations-cac', icon: 'file-signature' },
      ],
    },
    // Circuit inversé (ADR 0010 / PO-31) : le Président INITIE l'affectation (ouvre le plan, désigne les
    // contrôleurs, propose les réaffectations) puis SOUMET au Conseil pour validation. « توزيع مراقبي الدولة »
    // et « الإسناد » sont au glossaire ; « خطط الإسناد » est un libellé d'écran provisoire.
    {
      id: 'p-affectation',
      ar: 'توزيع مراقبي الدولة',
      fr: 'Affectation des contrôleurs',
      items: [
        // « خطط الإسناد » (p-plans-aff) retiré : l'affectation est désormais intégrée à la préparation du
        // plan de contrôle (workflow fusionné 2026-07-23) — plus de second menu/processus d'affectation.
        // Atelier CCE du 2026-10-01 : « المراقبون المتفرغون » et « قائمة مراقبي الدولة » ne font plus qu'UNE page —
        // la liste des contrôleurs porte le pilotage des affectations permanentes. L'ancienne adresse redirige.
        { id: 'p-controleurs', ar: 'قائمة مراقبي الدولة', fr: 'Liste des contrôleurs', route: '/presidence/controleurs', actifSur: ['/presidence/permanents'], icon: 'users' },
      ],
    },
  ];

  /**
   * Menu de l'espace Secrétariat CAEEP (كتابة لجنة التدقيق) — tenue du plan de charges des
   * commissaires aux comptes. Le CAC (مراقب الحسابات) est un professionnel EXTERNE membre de l'OECT :
   * cet espace n'a RIEN à voir avec l'affectation des contrôleurs d'État (glossaire §8).
   *
   * WORDING (Règle n°1) : `مراقبو الحسابات` fléchit un terme du glossaire. Le registre `حجم العمل`
   * remplace `أعباء`, recalé par le CCE ; `السقف` et `الطاقة` sont conservés. Ces libellés d'écran
   * restent [AR PROVISOIRE] hors glossaire — à faire dicter par le CCE.
   *
   * Icônes : réutiliser des noms Lucide DÉJÀ chargés ailleurs (un import d'icône dynamique en échec
   * vide le router-outlet en dev).
   */
  /*
   * Structure = la refonte arbitrée de docs/cac/navigation-espace-caeep.md §5.2 (N5 : carte
   * blanche), qui reprend les DEUX BLOCS de la note manuscrite du CCE. Seuls les écrans qui
   * existent entrent au menu — pas d'items morts.
   *
   * Le conflit ancien/nouveau est tranché par le rôle, pas par l'ancienneté :
   * — « المتبقّي بعد الإسناد » (bloc I) répond à QUI est éligible, à la date de nomination
   *   (moteur daté, RG-421) : c'est l'instruction.
   * — « الإسناد إلى جدول التعهدات » (bloc II, ex-« توزيع حجم العمل ») était l'écran de projection
   *   par cabinet ; RETIRÉ le 2026-09-22 (retour CAEEP) : l'agent y cherchait l'attribution des
   *   jours aux experts, qui vit dans le مخطط الأعباء. La tenue du référentiel passe par
   *   « مسك جدول التعهدات », l'instruction par le dossier.
   */
  readonly caeepGroups: readonly NavGroup[] = [
    {
      // « فضاء الوثائق والمعارف » — le MÊME espace documentaire que le contrôleur et le Président
      // (PO, 2026-09-07 : dans tous les espaces). Groupe d'accueil sans titre, comme ailleurs ; en
      // tête parce que le CCE place cette entrée en 3ᵉ position de l'espace contrôleur, avant le
      // métier. Le CAEEP y LIT (droit `documentary:read`, migration du même jour) ; il ne propose ni
      // ne valide.
      id: 'ce-accueil',
      items: [
        { id: 'ce-espace-doc', ar: 'فضاء الوثائق والمعارف', fr: 'Espace documentaire', route: '/caeep/espace-documentaire', icon: 'book-open' },
      ],
    },
    {
      // ══ RUBRIQUE I du menu demandé par le CCE ══════════════════════════════
      // « التدقيق في ملفات تعيين مراقبي الحسابات » — transcription littérale de la structure
      // remise par le Président (docs/cac/navigation-espace-caeep.md §1).
      //
      // Les trois files d'étape (قبول / تثبّت / عرض على اللجنة) ont d'abord été trois entrées de
      // menu, transcription littérale du document. À l'usage, l'agent a constaté que c'était TROIS
      // FOIS LE MÊME ÉCRAN — seul le `?etape=` changeait — et a demandé leur fusion (2026-08-19) :
      // une entrée « دراسة الملفات », les étapes devenant des onglets DANS la page. Le paramètre
      // `?etape=` survit tel quel (liens profonds, catalogue d'aide) ; il n'est qu'un regroupement
      // des statuts déjà portés par `cac_nomination` — aucune règle métier n'en dépend.
      //
      // Les sous-entrées I·3·a/b/c (عرض الملف, محضر اللجنة, جدول الترتيب النهائي) ne figurent pas
      // ici : elles se rapportent toujours à UN dossier et s'ouvrent depuis lui. Un menu ne peut pas
      // ouvrir « la vérification » sans savoir de quel dossier il s'agit.
      id: 'ce-audit',
      ar: 'التدقيق في ملفات تعيين مراقبي الحسابات',
      fr: 'Audit des dossiers de nomination des commissaires aux comptes',
      items: [
        // I·1+I·2+I·3 fusionnées — « دراسة الملفات » (wording hors glossaire, à ratifier CCE, comme
        // l'étaient les trois libellés qu'il remplace). `queryAgnostic` : l'entrée reste allumée
        // pendant la navigation entre onglets. `eteinteSur` : sur `?etape=DECISION`, seule « قرار التعيين »
        // (groupe المتابعة) s'allume — l'effet de bord « deux entrées allumées » est levé (L4).
        // Pastille `caeep.dossiers` (revue du 2026-09-29) : les dossiers qui pressent, calculés comme la
        // zone « ما الذي يستعجل » du tableau de bord.
        { id: 'ce-cac-dossiers', ar: 'دراسة الملفات', fr: 'Instruction des dossiers', route: '/caeep/nominations', queryAgnostic: true, eteinteSur: { etape: 'DECISION' }, icon: 'inbox', badgeKey: 'caeep.dossiers' },
        // I·3·b « محضر اللجنة » — marqué « ❌ à créer » dans navigation-espace-caeep.md jusqu'ici.
        // Mène aux MÊMES écrans que les séances du Conseil, montés sous l'espace CAEEP : le serveur
        // filtre par type de séance, si bien que le secrétariat n'y voit que sa commission. Sans icône
        // délibérément — voir le piège Lucide plus bas.
        { id: 'ce-cac-seances', ar: 'جلسات اللجنة', fr: 'Séances de la commission', route: '/caeep/seances', exact: true, icon: 'calendar' },
        // Pastille `caeep.pvSeances` : les PV de la commission en attente de validation ou de signature — le geste du
        // Président, qui y vient depuis le sélecteur d'espaces (il n'a plus de file de PV dans son propre espace).
        { id: 'ce-cac-pv', ar: 'محاضر اللجنة', fr: 'PV de la commission', route: '/caeep/seances/pv', icon: 'file-text', badgeKey: 'caeep.pvSeances' },
        // Hors structure CCE mais demande explicite du 30/07 : les nominations d'un même jour,
        // cumulées par cabinet (RG-425). C'est un contrôle de la fiche de désignation.
        { id: 'ce-cac-lot', ar: 'التعيينات المبرمجة', fr: 'Nominations programmées', route: '/caeep/instruction-lot', icon: 'calendar-days' },
      ],
    },
    {
      // ══ RUBRIQUE II du menu demandé ════════════════════════════════════════
      // « المتابعة ». Le Président y place deux entrées ; les cinq autres écrans du SI y sont
      // rattachés au plus proche (option retenue avec l'utilisateur le 2026-08-03) plutôt que
      // supprimés — ce sont ses propres demandes : les mandats échus viennent de la séance du 30/07,
      // la capacité du vivier d'ARB-03.
      id: 'ce-suivi',
      ar: 'المتابعة',
      fr: 'Suivi',
      items: [
        // II·1 — « مسك جدول التعهدات » est AU GLOSSAIRE (annexe 12) : l'ancien « مخطط حجم العمل »
        // confondait جدول التعهدات (vue cabinet) et مخطط الأعباء (vue expert), piège documenté.
        { id: 'ce-cac-plan', ar: 'مسك جدول التعهدات', fr: 'Tenue des plans de charge', route: '/caeep/plan-charges', icon: 'briefcase' },
        // II·2·b — le pendant EXPERT, que RG-462 exige au même titre : le plafond individuel de 70 j
        // (RG-401) et la moyenne du départage (RG-340) portent sur l'expert, pas sur le cabinet.
        // `user` et non `user-round` : le provider d'`app.config.ts` enregistre une liste EXPLICITE
        // d'icônes, et une icône absente de cette liste lève « Unable to resolve icon » à chaque rendu
        // du menu. Vérifier la présence du nom dans le bundle @lucide ne prouve rien — c'est
        // l'enregistrement auprès du provider qui compte.
        { id: 'ce-cac-charge-exp', ar: 'مخطط الأعباء', fr: 'Plan de charges par expert', route: '/caeep/charge-experts', icon: 'file-spreadsheet' },
        // II·2·c — le référentiel des experts-comptables (2026-09-25) : la personne derrière les deux
        // vues précédentes, et la file des identités que la source contredit. « الخبراء المحاسبون » :
        // pluriel de خبير محاسب (glossaire) — l'entrée est à ratifier par le CCE avec l'écran.
        { id: 'ce-cac-experts', ar: 'الخبراء المحاسبون', fr: 'Experts-comptables', route: '/caeep/experts', icon: 'user-check' },
        // II·2 — « قرار التعيين » : la décision, de la proposition CAEEP à la nomination déclarée
        // par l'EEP. La بطاقة تعيين (II·2·a) s'ouvre depuis le dossier.
        // Premier filet de « المتابعة » : la grappe de la décision et de la charge qu'elle engage, en tête
        // de laquelle reste l'entrée II·2 du Président.
        { id: 'ce-cac-decision', ar: 'قرار التعيين', fr: 'Décision de nomination', route: '/caeep/nominations', query: { etape: 'DECISION' }, icon: 'file-signature', separeAvant: true },
        // PLUS D'ENTRÉE « الإسناد إلى جدول التعهدات » (retour CAEEP du 2026-09-22, arbitrage PO) :
        // l'écran de projection par cabinet qu'elle ouvrait est retiré, et le geste qu'elle
        // désignait pour l'agent — répartir les jours d'un mandat entre les experts — vit dans le
        // مخطط الأعباء, déjà au menu. Une seconde entrée vers la même page aurait été une
        // duplication. L'adresse /caeep/affectation redirige encore, pour les favoris.
        // Rattachées ici (option B) : toutes disent l'état du référentiel ou de la charge.
        { id: 'ce-cac-echus', ar: 'المهام المنتهية', fr: 'Mandats échus', route: '/caeep/mandats-echus', icon: 'clock-alert', badgeKey: 'caeep.echus' },
        { id: 'ce-cac-vivier', ar: 'طاقة استيعاب المكاتب', fr: 'Capacité du vivier', route: '/caeep/capacite-vivier', icon: 'bar-chart-3' },
        // PAS D'ENTRÉE pour « المتبقّي بعد الإسناد » (/caeep/eligibilite), et c'est délibéré : ce
        // calcul suppose une EEP ET une date de nomination (RG-421). Ouvert sans elles depuis un
        // menu, l'écran ne peut rien calculer et n'affiche qu'un renvoi vers « المهام المنتهية » —
        // une entrée qui ne mène à rien d'utilisable est pire qu'absente, parce qu'elle fait croire à
        // une fonctionnalité vide. Son point d'entrée est le lien « المكاتب المتاحة » de chaque ligne
        // de mandat échu, qui lui passe l'EEP et une date de libération plausible.
        // Même raison que pour les sous-entrées I·3·a/b/c, absentes du menu du CCE lui aussi.
        { id: 'ce-cac-eep', ar: 'حجم العمل حسب المؤسسة', fr: 'Charge par EEP', route: '/caeep/eep', icon: 'building-2' },
        // Second filet : la veille — les constats et le tableau de bord, page d'accueil de l'espace.
        { id: 'ce-cac-anomalies', ar: 'اختلالات مرصودة', fr: 'Anomalies détectées', route: '/caeep/anomalies', icon: 'alert-triangle', badgeKey: 'caeep.anomalies', separeAvant: true },
        { id: 'ce-cac-pilotage', ar: 'قيادة النشاط', fr: 'Pilotage', route: '/caeep/pilotage', icon: 'layout-dashboard' },
        // Le rapport d'activité de la commission — annuel, trimestriel, mensuel, PDF et DOCX (agent CAEEP, 2026-10-31) :
        // le chapitre CAEEP du rapport annuel du CCE, produit ici. « تقارير نشاط اللجنة » : sur « تقرير النشاط الثلاثي »
        // du glossaire — à ratifier par le CCE.
        { id: 'ce-cac-rapports', ar: 'تقارير نشاط اللجنة', fr: 'Rapports d’activité de la commission', route: '/caeep/rapports-activite', icon: 'chart-column' },
      ],
    },
    {
      // La configuration reste à part : c'est la base du système, pas une étape du travail
      // (décision de l'utilisateur, 2026-08-03). Barème des honoraires et paramètres d'exercice —
      // d'où sortent les plafonds que le SI oppose.
      //
      // Sans en-tête depuis la revue du 2026-09-29 : le groupe ne porte qu'une entrée du même nom, et
      // l'en-tête répétait l'item. Un filet le sépare (groupe sans titre après un autre groupe).
      id: 'ce-systeme',
      items: [
        { id: 'ce-cac-param', ar: 'إعدادات المنظومة', fr: 'Configuration', route: '/caeep/parametrage', icon: 'sliders-horizontal' },
      ],
    },
  ];

  /**
   * Menu de l'espace Conseil (مجلس الهيئة) — VALIDATION du plan d'affectation des contrôleurs
   * (توزيع مراقبي الدولة) : le Conseil valide (= publication) ou renvoie au Président ce que celui-ci a
   * préparé et soumis (circuit inversé, ADR 0010 / PO-31). « المصادقة » et « الإسناد » sont au glossaire ;
   * « خطط الإسناد » est un libellé d'écran provisoire (statuts d'affectation non figés au glossaire).
   */
  readonly conseilGroups: readonly NavGroup[] = [
    {
      id: 'cn-accueil',
      items: [
        { id: 'cn-dashboard', ar: 'لوحة القيادة', fr: 'Tableau de bord', route: '/conseil/dashboard', icon: 'layout-dashboard' },
      ],
    },
    // Le cycle de vie d'une réunion (PV du 24/07/2026) : برمجة الجلسة → سير الجلسة → محضر الجلسة. Depuis la refonte du
    // 2026-10-05 (CCE), ce sont les trois ÉTAPES d'une même page de séance (en-tête commun `app-seance-etapes`), et non
    // plus trois entrées de menu qui se chevauchaient (une séance convoquée apparaissait sous deux d'entre elles).
    // `وثائق العمل` = les pièces jointes d'une réunion (précision du CCE, 29/07) : un panneau de la séance.
    {
      id: 'cn-seances',
      ar: 'أشغال المجلس',
      fr: 'Travaux du Conseil',
      items: [
        // Refonte du 2026-10-05 (CCE) : UNE entrée pour les séances — chacune s'ouvre sur ses trois étapes (برمجة الجلسة ─
        // سير الجلسة ─ محضر الجلسة), qui étaient trois entrées de menu se chevauchant. `exact` : l'entrée reste allumée sur
        // les étapes d'une séance, et cède à « محاضر الجلسة » le seul sous-arbre `/pv` (même règle que « جلسات اللجنة »).
        // « وثائق العمل » n'est plus une entrée : les pièces vivent dans la séance. « محاضر الجلسة » reste : c'est la
        // file du Président (pastille des PV à signer).
        // « جلسات المجلس » : forme calquée sur « جلسات اللجنة » (glossaire §8) — provisoire, à ratifier CCE.
        { id: 'cn-seances-liste', ar: 'جلسات المجلس', fr: 'Séances du Conseil', route: '/conseil/seances', exact: true, icon: 'calendar' },
        { id: 'cn-seances-pv', ar: 'محاضر الجلسة', fr: 'Procès-verbaux', route: '/conseil/seances/pv', icon: 'file-text', badgeKey: 'conseil.pvSeances' },
        // Le registre des décisions et leur suivi (2026-10-05) — libellé provisoire, à ratifier CCE.
        { id: 'cn-decisions', ar: 'سجل قرارات المجلس', fr: 'Registre des décisions', route: '/conseil/decisions', icon: 'list-checks' },
        // « الوثائق » (bibliothèque documentaire, CONS-18) retirée de l'espace Conseil le 2026-09-16 (retour CCE) ;
        // le droit ged:read est conservé, la bibliothèque reste servie aux espaces contrôleur et Président.
      ],
    },
    {
      id: 'cn-affectation',
      ar: 'المصادقة على الإسناد',
      fr: "Validation des affectations",
      items: [
        // Workflow fusionné (2026-07-23) : la validation du Conseil (« خطط الإسناد » ci-dessous) publie
        // désormais le plan de contrôle ET les affectations en une seule fois → l'ancien écran de
        // délibération du plan seul (cn-plans-controle → /conseil/plans) est retiré.
        // `جدول التعيينات` et non `خطط الإسناد` (glossaire §8.7, CCE 27/08). Le terme n'était
        // pas au glossaire — il était marqué « libellé d'écran provisoire » — et le CCE l'a tranché.
        { id: 'cn-plans', ar: 'جدول التعيينات', fr: "Plans d'affectation", route: '/conseil/affectations', icon: 'git-branch', badgeKey: 'conseil.plans' },
        { id: 'cn-reaffectations', ar: 'إعادة الإسناد', fr: 'Réaffectations', route: '/conseil/reaffectations', icon: 'user-check', badgeKey: 'conseil.reaffectations' },
      ],
    },
    // Le groupe « مراقبو الحسابات » est RETIRÉ de l'espace Conseil — arbitrage du CCE, 2026-08-08 :
    // aucun lien entre le Conseil et la commission, donc ni création, ni séance, ni listing CAC ici.
    //
    // ⚠️ Il portait l'unique chemin par lequel le Conseil exerçait `cac-nomination:valider` — le
    // « processus 05, phase 5 » : le secrétariat instruit, le CONSEIL valide et retient, le Président
    // signe. Le Conseil est le SEUL rôle porteur de cette permission, et un dossier attend en
    // `PROPOSE_CAEEP`. Retirer l'entrée sans transférer la validation laisse l'étape sans acteur : le
    // défaut « la permission sans l'écran » se produit ici à l'envers, et il bloque un circuit réel.
    // Question portée au CCE avec ce lot : qui valide désormais — le Président, qui signe déjà ?
    // D11 : le Conseil sort du circuit de validation des rapports du contrôleur (annuel + trimestriel) ;
    // le Président devient le validateur unique → le groupe « Rapports » du Conseil est retiré.
  ];

  /**
   * Menu de l'espace Cellule administrative (الخلية الإدارية) — AVAL : notification des EEP +
   * suivi des accusés de réception (rôle canon : logistique, lecture sur l'affectation).
   */
  readonly celluleGroups: readonly NavGroup[] = [
    {
      id: 'ca-accueil',
      items: [
        { id: 'ca-dashboard', ar: 'لوحة القيادة', fr: 'Tableau de bord', route: '/cellule/dashboard', icon: 'layout-dashboard' },
        // « فضاء الوثائق والمعارف » dans tous les espaces (PO, 2026-09-07). La Cellule y administre
        // déjà la taxonomie (`documentary:write`) : l'entrée manquait, pas le droit.
        { id: 'ca-espace-doc', ar: 'فضاء الوثائق والمعارف', fr: 'Espace documentaire', route: '/cellule/espace-documentaire', icon: 'book-open' },
      ],
    },
    {
      id: 'ca-institutionnel',
      ar: 'التصرف المؤسساتي',
      fr: 'Gestion institutionnelle',
      items: [
        { id: 'ca-controleurs', ar: 'قائمة مراقبي الدولة', fr: "Annuaire des contrôleurs", route: '/cellule/controleurs', icon: 'users' },
        // Registre des affectations (2026-09-24) : rectifier sans renvoyer de classeur — « سجل التعيينات » à ratifier CCE.
        { id: 'ca-affectations', ar: 'سجل التعيينات', fr: 'Registre des affectations', route: '/cellule/affectations', icon: 'list-checks' },
        // Les تعويض مؤقت et leurs actes, en lecture : la cellule prépare et archive le courrier du Président (27/10). [AR PROVISOIRE.]
        { id: 'ca-remplacements', ar: 'متابعة التعويض المؤقت', fr: 'Suivi des remplacements', route: '/cellule/remplacements', icon: 'refresh-cw' },
        // Gestion des comptes (07/09) : les agents, leurs rôles, l'activation, le mot de passe — libellé AR provisoire (à ratifier).
        { id: 'ca-comptes', ar: 'الحسابات والصلاحيات', fr: 'Comptes et habilitations', route: '/cellule/comptes', icon: 'user-check' },
        { id: 'ca-formation', ar: 'التكوين', fr: 'Formation', route: '/cellule/formations', icon: 'graduation-cap' },
        { id: 'ca-cooperation', ar: 'التعاون الدولي', fr: 'Coopération internationale', route: '/cellule/cooperation', icon: 'globe' },
        // Renommé « مسالك المراقبة » (demande du 2026-09-16, ex-« بطاقات الحضور ») — hors glossaire, à ratifier CCE ;
        // le titre de la page reste « إسناد بطاقات الحضور » (règle AD-01), le FR n'est pas retouché.
        { id: 'ca-primes', ar: 'مسالك المراقبة', fr: 'Jetons de présence', route: '/cellule/primes', icon: 'coins' },
        // Restitution institutionnelle (CELL-13) : les chiffres des trois domaines sous un seul
        // exercice. ⚠️ « الحصيلة السنوية » hors glossaire — à ratifier CCE.
        { id: 'ca-bilan', ar: 'الحصيلة السنوية', fr: 'Bilan annuel', route: '/cellule/bilan', icon: 'bar-chart-3' },
        // « قاعدة بيانات المؤسسات » (document CCE du 2026-09-04) — même page que côté Président et Reporting.
        // La cellule n'avait aucun accès aux EEP : `enterprise:read` ALL lui est accordé par la migration
        // 20260904153523 (lecture seule), même arbitrage que pour la Cellule de Reporting le 2026-08-20.
        { id: 'ca-base-donnees', ar: 'قاعدة بيانات المؤسسات', fr: 'Base de données des EEP', route: '/cellule/base-donnees', icon: 'database' },
      ],
    },
  ];

  /**
   * Espace Cellule de Reporting (espace 06).
   *
   * <p>Ce menu ne liste QUE des écrans dont le périmètre est acté au backlog 06. Il annonçait
   * auparavant les 8 écrans de la spec §5 ; trois ont été retirés le 2026-08-20 parce qu'aucun
   * périmètre ne les porte :
   * <ul>
   *   <li><b>تهيئة لوحات القيادة</b> (Config dashboards) — libellé INTROUVABLE dans `docs/`, et le
   *       backlog 06 le renvoie au backlog 07 où il n'existe pas. Aucun item, nulle part ;
   *       ses tables (`reporting.dashboard`, `dashboard_widget`) n'ont jamais reçu une ligne.</li>
   *   <li><b>الأرشيف</b> (Archives) — reporté « avec la section 2 » sans item propre, et le terme
   *       n'est pas ratifié pour le reporting (il l'est pour la passation et la messagerie).</li>
   *   <li><b>التقرير العام لهيئة مراقبي الدولة</b> — vidé par la <b>décision D1 du 2026-06-22</b> : le rapport
   *       institutionnel est rédigé HORS plateforme. Le SI expose des chiffres, il n'assemble pas.
   *       L'éditeur collaboratif décrit en spec §5.3 et dans `processes/07` est de l'intention
   *       PÉRIMÉE, jamais mise à jour après D1 — ne pas le réintroduire sur la foi de ces documents.</li>
   *   <li><b>المؤشرات</b> (catalogue de ce que le SI sait mesurer) et <b>الوثائق</b> (bibliothèque
   *       GED) — RETIRÉES le 2026-09-04 à la demande de la Cellule de Reporting, avec leurs routes et
   *       la page du catalogue (`features/reporting/indicateurs`, supprimée). Le registre sémantique
   *       reste documenté par l'explorateur lui-même (notes, formules) ; la GED reste accessible à la
   *       cellule par l'API (`ged:read` ALL, migration 20260820131106) mais n'a plus d'écran ici.</li>
   * </ul>
   * Une entrée grisée ou retirée est un engagement : la remettre demande d'abord un item de backlog.
   *
   * <p>Wording (Règle n°1) — ratifié au glossaire : `لوحة القيادة` (§4bis). Donnés par la Cellule de
   * Reporting (réunion interne du 2026-09-28, glossaire §8.32) : `تقارير مراقبي الدولة` (ex-`التقارير
   * الجارية`), `بحث حسب الطلب` (ex-`تصدير حسب الطلب`), et l'ordre du menu.
   *
   * <p>⚠️ Icônes : `LucideDynamicIcon` les charge par import() dynamique, mais seule compte la liste
   * EXPLICITE de `app.config.ts` (`provideLucideIcons`). Un nom absent de cette liste lève à chaque
   * rendu du menu, et l'exception avorte la passe de change-detection partagée → le router-outlet
   * reste vide en dev (zoneless ne relance pas la CD ; symptôme vécu sur `/reporting/dashboard`).
   * Avant d'ajouter une icône ici, VÉRIFIER qu'elle est enregistrée dans `app.config.ts`.
   */
  readonly reportingGroups: readonly NavGroup[] = [
    {
      // Une seule liste, dans l'ordre demandé par la Cellule (réunion interne du 2026-09-28) : les
      // intertitres « الإنتاج / الموارد » ne collaient plus à cet ordre, qui mêle les deux.
      // « التقارير الخصوصية » (rapports ظرفية et دورية de la هيئة) : sous التقرير العام, comme le CCE l'a demandé le
      // 2026-10-20 — des rapports rédigés hors du SI, que la Cellule y dépose.
      id: 'rp-menu',
      items: [
        // §5.1 Dashboard Reporting → /reporting/dashboard (CÂBLÉ) — REP-07
        { id: 'rp-dashboard', ar: 'لوحة القيادة', fr: 'Tableau de bord', route: '/reporting/dashboard', icon: 'layout-dashboard' },
        // « قاعدة بيانات المؤسسات » (document CCE du 2026-09-04) — même page que côté Président.
        { id: 'rp-base-donnees', ar: 'قاعدة بيانات المؤسسات', fr: 'Base de données des EEP', route: '/reporting/base-donnees', icon: 'database' },
        // « فضاء الوثائق والمعارف » dans tous les espaces (PO, 2026-09-07). ⚠️ Ce n'est PAS le retour
        // de « الوثائق » retiré le 2026-09-04 (PR #268) : celle-là était une bibliothèque propre à
        // l'espace ; celui-ci est l'espace PARTAGÉ du SI, le même que lit le contrôleur. Lecture
        // seule (`documentary:read`, migration du même jour).
        { id: 'rp-espace-doc', ar: 'فضاء الوثائق والمعارف', fr: 'Espace documentaire', route: '/reporting/espace-documentaire', icon: 'book-open' },
        // §5.2 → /reporting/instances (CÂBLÉ) — REP-07, lecture transverse. « تقارير مراقبي الدولة » (Cellule,
        // 2026-09-28) : les rapports ANNUELS et TRIMESTRIELS des contrôleurs, en cours comme achevés — la
        // page filtre déjà par type et par statut. Il s'appelait « التقارير الجارية ».
        { id: 'rp-instances', ar: 'تقارير مراقبي الدولة', fr: 'Rapports des contrôleurs', route: '/reporting/instances', icon: 'file-clock' },
        // Rapport Général (التقرير العام) — les constats du الجزء الثالث §1, calculés depuis la grille
        // de contrôle interne (étude du 2026-09-16, lot 1), les chiffres, les annexes et le document.
        { id: 'rp-rapport-general', ar: 'التقرير العام', fr: 'Rapport Général', route: '/reporting/rapport-general', icon: 'file-text' },
        { id: 'rp-rapports-specifiques', ar: 'التقارير الخصوصية', fr: 'Rapports spécifiques', route: '/reporting/rapports-specifiques', icon: 'file-archive' },
        // « المؤشرات » (§5.4) vivait ici jusqu'au 2026-09-04 — retirée à la demande de la Cellule
        // (voir la javadoc). §5.6 Explorateur 360 / pivot ad hoc (CÂBLÉ — module 12 §15.6, ADR 0011) —
        // REP-01→05. Le menu dit enfin comme la page « بحث حسب الطلب » (retours wording du 2026-09-22).
        { id: 'rp-exports', ar: 'بحث حسب الطلب', fr: 'Recherche personnalisée', route: '/reporting/explorateur', icon: 'sliders' },
        // Référentiel des EEP (CÂBLÉ) — comme côté Président (demande du 2026-08-20). Libellé et
        // icône repris à l'identique de `p-entreprises` : la donnée est la même.
        { id: 'rp-entreprises', ar: 'المؤسسات والمنشآت العمومية', fr: 'Établissements et entreprises publics', route: '/reporting/entreprises', icon: 'building-2' },
        // Annuaire des contrôleurs (CÂBLÉ) — demande du responsable d'espace, 2026-08-20 : le MÊME
        // écran et le MÊME accès que côté Président. Libellé et icône repris à l'identique des deux
        // espaces qui le montent déjà (`p-controleurs`, `ca-controleurs`) : deux menus qui
        // nommeraient différemment la même donnée feraient douter de laquelle est juste.
        { id: 'rp-controleurs', ar: 'قائمة مراقبي الدولة', fr: 'Annuaire des contrôleurs', route: '/reporting/controleurs', icon: 'users' },
        // « الوثائق » (§5.8, GED-08) vivait ici jusqu'au 2026-09-04 — retirée à la demande de la
        // Cellule (voir la javadoc). Le composant BibliothequePage continue de servir les trois
        // autres espaces.
      ],
    },
  ];

  /**
   * Menu du 9ᵉ espace « Qualité & Évaluation » (TR-03, CR 2026-07-24).
   *
   * WORDING — l'arabe n'est renseigné que là où il est ÉTABLI : `مراقبة جودة العمل الرقابي`,
   * `عينة` et `الفحص المعمق والمستندي` sont VERBATIM des référentiels du CCE. « Matrice de
   * conformité » n'a aucun équivalent arabe ratifié : le français reste affiché dans les deux
   * langues, comme le précédent du « jeton de présence ». Voir
   * `docs/qualite/proposition-glossaire-section9.md`.
   */
  readonly qualiteGroups: readonly NavGroup[] = [
    {
      id: 'q-accueil',
      items: [
        { id: 'q-campagnes', ar: 'مراقبة جودة العمل الرقابي', fr: 'Contrôle de la qualité du travail des contrôleurs', route: '/qualite/campagnes', icon: 'layout-dashboard' },
      ],
    },
    {
      id: 'q-cycle',
      ar: 'الفحص المعمق والمستندي',
      fr: 'Examen documentaire',
      items: [
        { id: 'q-revues', ar: 'الفحص المعمق والمستندي', fr: 'Revues de dossiers', route: '/qualite/revues', icon: 'file-check' },
      ],
    },
  ];

  /** Groupe unique (sans en-tete) pour les 9 espaces — l'intitule global suffit. */
  private readonly espacesGroups: readonly NavGroup[] = [{ id: 'espaces', items: this.spaces }];

  /** L'entrée s'éteint-elle sur l'URL courante (paramètres `eteinteSur`) ? Lu depuis le signal d'URL du menu. */
  eteinte(item: NavLink): boolean {
    const regle = item.eteinteSur;
    if (!regle) {
      return false;
    }
    const u = this.url();
    const i = u.indexOf('?');
    if (i < 0) {
      return false;
    }
    const q = new URLSearchParams(u.slice(i + 1).split('#')[0]);
    return Object.entries(regle).every(([k, v]) => q.get(k) === v);
  }

  /** true si l'utilisateur se trouve dans l'espace Contrôleur. */
  private readonly inControleur = computed(() => this.url().startsWith('/controleur'));

  /** true si l'utilisateur se trouve dans l'espace Présidence. */
  private readonly inPresidence = computed(() => this.url().startsWith('/presidence'));

  /** true si l'utilisateur se trouve dans l'espace Conseil / Rapporteur. */
  private readonly inConseil = computed(() => this.url().startsWith('/conseil'));

  /** true si l'utilisateur se trouve dans l'espace Cellule administrative. */
  private readonly inCellule = computed(() => this.url().startsWith('/cellule'));

  /** true si l'utilisateur se trouve dans l'espace Cellule de Reporting. */
  private readonly inReporting = computed(() => this.url().startsWith('/reporting'));

  /** Espace Secrétariat CAEEP (كتابة لجنة التدقيق) — plan de charges des CAC. */
  private readonly inCaeep = computed(() => this.url().startsWith('/caeep'));

  /** 9ᵉ espace — contrôle qualité périodique et par échantillonnage (TR-03). */
  private readonly inQualite = computed(() => this.url().startsWith('/qualite'));

  /**
   * Espaces atteignables par un changement d'espace, avec les rôles realm que chacun admet.
   *
   * <p><b>Le rôle est la seule clé.</b> Le Président porte, en plus du sien, les rôles des espaces qu'il
   * visite (`CONSEIL_CCE`, `CELLULE_ADMINISTRATIVE`, `CELLULE_REPORTING`) : il y agit exactement comme
   * le titulaire de l'espace, sans permission ni écran dédiés. Ce sélecteur ne fait que <b>naviguer</b>
   * — il n'autorise rien. L'autorité reste le {@code roleGuard} de la route puis l'{@code AccessGuard}
   * du serveur, qui rend 404 hors portée.
   *
   * <p><b>PLUSIEURS rôles par espace</b>, et non un seul, parce que les gardes de route en admettent
   * déjà plusieurs. L'espace CAEEP en est le cas : sa route est gardée par {@code CAC_ROLES}, qui
   * accepte `SECRETARIAT_CAEEP` <b>et</b> `PRESIDENT_CCE` — le Président pouvait donc déjà ouvrir ses
   * dix-neuf écrans en tapant l'adresse, mais le sélecteur ne les lui proposait pas. Le lister ici
   * n'accorde AUCUN droit nouveau ; cela rend visible un accès qui existait (demande du 2026-08-08).
   *
   * <p>⚠️ Voir n'est pas agir : le Président détient 3 des 6 permissions du secrétariat. Il lui manque
   * `cac-cabinet:read`, `cac-nomination:instruire` et surtout `cac:manage` — que l'arbitrage A6 réserve
   * au secrétariat, seul à tenir le plan de charges. Les écrans qui en dépendent resteront donc en
   * retrait pour lui : c'est voulu, et cela relève d'un arbitrage, pas d'une décision d'écran.
   *
   * <p>Les libellés sont repris verbatim de {@link spaces} (glossaire). La liste est volontairement
   * distincte : {@link spaces} sert aussi de menu latéral et porte 3 placeholders sans route, qui n'ont
   * rien à faire ici — une entrée qui ne mène nulle part est pire qu'absente.
   *
   * <p>Le préfixe d'URL est <b>dérivé</b> de la route (`/presidence/dashboard` → `presidence`) : un
   * champ de plus serait un second endroit à tenir à jour, et donc à désynchroniser.
   */
  private readonly espacesCommutables: readonly (NavLink & { readonly roles: readonly string[]; readonly route: string })[] = [
    { id: 'presidence', roles: ['PRESIDENT_CCE'], ar: 'رئاسة الهيئة', fr: 'Présidence du CCE', route: '/presidence/dashboard', icon: 'crown' },
    { id: 'conseil', roles: ['CONSEIL_CCE', 'SECRETARIAT_CONSEIL'], ar: 'أعضاء مجلس الهيئة', fr: 'Membres du Conseil', route: '/conseil/dashboard', icon: 'users' },
    { id: 'cellule-admin', roles: ['CELLULE_ADMINISTRATIVE'], ar: 'الخلية الإدارية', fr: 'Cellule Administrative', route: '/cellule/dashboard', icon: 'building' },
    { id: 'reporting', roles: ['CELLULE_REPORTING'], ar: 'خلية إعداد التقارير', fr: 'Cellule de Reporting', route: '/reporting/dashboard', icon: 'bar-chart-3' },
    // Les mêmes rôles que `CAC_ROLES` dans `app.routes.ts`, moins `ADMIN_FONCTIONNEL` : il n'a pas
    // d'espace propre au sélecteur. Toute divergence entre ces deux listes se verrait comme un espace
    // proposé qui rejette, ou un accès réel que rien n'annonce.
    { id: 'sec-caeep', roles: ['SECRETARIAT_CAEEP', 'PRESIDENT_CCE'], ar: 'كتابة لجنة التدقيق في حسابات المؤسسات والمنشآت العمومية', fr: 'Secrétariat de la CAEEP', route: '/caeep/pilotage', icon: 'file-check' },
    { id: 'controleurs', roles: ['CONTROLEUR_ETAT'], ar: 'مراقبو الدولة', fr: "Contrôleurs d'État", route: '/controleur/dashboard', icon: 'user-check' },
    // L'espace qualité manquait : un contrôleur membre de l'équipe qualité (réviseur) n'avait qu'un espace listé,
    // donc aucun sélecteur, et restait enfermé dans l'un ou l'autre (PO, 2026-10-12). Mêmes rôles que la garde
    // de `qualite/campagnes`, moins le Président, qui y entre par son propre espace.
    { id: 'qualite', roles: ['EQUIPE_QUALITE', 'CELLULE_REVISION_INTERNE'], ar: 'مراقبة جودة العمل الرقابي', fr: 'Contrôle de la qualité du travail des contrôleurs', route: '/qualite/campagnes', icon: 'list-checks' },
  ];

  /** Espaces réellement ouverts au porteur du jeton — le filtre suit les rôles, rien d'autre. */
  readonly espacesAccessibles = computed(() => {
    const portes = this.roles.roles();
    return this.espacesCommutables.filter((e) => e.roles.some((r) => portes.includes(r)));
  });

  /** L'espace où l'on se trouve, reconnu au premier segment de l'URL. `null` hors des sept. */
  readonly espaceCourant = computed(() => {
    const u = this.url();
    return this.espacesCommutables.find((e) => u.startsWith(`/${segmentDeTete(e.route)}`)) ?? null;
  });

  /**
   * Le sélecteur n'apparaît qu'à partir de DEUX espaces : avec un seul, il n'offrirait aucun choix et
   * n'afficherait qu'un intitulé — un bouton qui ne fait rien. Les comptes mono-rôle, c'est-à-dire tous
   * sauf le Président, ne le voient donc pas.
   */
  readonly showSpaceSwitcher = computed(() => this.espacesAccessibles().length > 1);

  /**
   * Cible du « voir tout » du panneau : la page « كل الإشعارات », commune à tous les espaces.
   *
   * <p>Le lien menait au TABLEAU DE BORD de l'espace courant : il n'existait aucune page de notifications,
   * et tout ce qui dépassait les 50 plus récentes était inatteignable (revue du 2026-09-16).
   */
  readonly notificationsViewAllLink = '/notifications';

  /** Groupes de navigation affiches selon le contexte (présidence / conseil / cellule / reporting / contrôleur / 8 espaces). */
  readonly navGroups = computed<readonly NavGroup[]>(() =>
    this.inQualite()
      ? this.qualiteGroups
      : this.inCaeep()
      ? this.caeepGroups
      : this.inPresidence()
        ? this.presidenceGroups
        : this.inConseil()
          ? this.conseilGroups
          : this.inCellule()
            ? this.celluleGroups
            : this.inReporting()
              ? this.reportingGroups
              : this.inControleur()
                ? this.controleurGroups
                : this.espacesGroups,
  );

  /**
   * Ce que la recherche globale propose comme rubriques : le menu de l'espace courant, puis les AUTRES
   * espaces du porteur pour en changer au clavier.
   *
   * <p>Hors espace (menu générique des neuf espaces), on ne reprend PAS ce menu : il liste des espaces
   * que l'utilisateur n'a pas. La recherche ne propose que les espaces que ses rôles lui ouvrent — le
   * même filtre que le sélecteur d'espace.
   */
  readonly rubriquesRecherche = computed<readonly GroupeRubriquesSource[]>(() => {
    const menu = this.navGroups();
    const courant = this.espaceCourant()?.id;
    const autres = this.espacesAccessibles().filter((e) => e.id !== courant);
    const texts = this.uiTexts();
    const groupes: GroupeRubriquesSource[] = menu === this.espacesGroups ? [] : [...menu];
    if (autres.length) {
      groupes.push({ id: 'rg-espaces', ar: texts.spacesTitle, fr: texts.spacesTitle, items: autres, separe: true });
    }
    return groupes;
  });

  /** L'espace de l'URL (premier segment), pour la recherche globale : c'est lui qui cloisonne ses résultats. */
  readonly espaceUrl = computed(() => espaceDepuisUrl(this.url(), ''));

  /** Intitule de section de la sidebar (bilingue), selon le contexte. */
  readonly sectionLabel = computed(() => {
    if (this.inQualite()) {
      // AR verbatim du référentiel du CCE du 03/06/2026 ; le nom français de l'espace n'est pas ratifié.
      return this.lang() === 'ar' ? 'مراقبة جودة العمل الرقابي' : 'Contrôle de la qualité du travail des contrôleurs';
    }
    if (this.inPresidence()) {
      return this.lang() === 'ar' ? 'فضاء رئيس الهيئة' : 'Espace Président';
    }
    if (this.inConseil()) {
      return this.lang() === 'ar' ? 'فضاء مجلس هيئة مراقبي الدولة' : 'Espace Conseil';
    }
    if (this.inCellule()) {
      return this.lang() === 'ar' ? 'فضاء الخلية الإدارية' : 'Espace Cellule administrative';
    }
    if (this.inReporting()) {
      return this.lang() === 'ar' ? 'فضاء خلية إعداد التقارير' : 'Espace Cellule de Reporting';
    }
    if (this.inControleur()) {
      return this.lang() === 'ar' ? 'فضاء مراقب الدولة' : "Espace Contrôleur d'État";
    }
    return this.lang() === 'ar' ? 'الفضاءات' : 'Espaces';
  });

  /**
   * Gèle les transitions du shell le temps d'un rendu.
   *
   * <p>Le tiroir fermé est poussé hors écran par {@code translateX(±100%)} : le signe suit la
   * direction. Basculer AR⇄FR change donc ce signe, et la transition INTERPOLE entre les deux — le
   * menu traverse l'écran de part en part, en passant par le milieu, alors qu'il est censé rester
   * caché. On coupe l'animation pour ce rendu-là : la direction change, rien ne bouge.
   */
  readonly transitionsGelees = signal(false);

  /** Bascule la langue AR <-> FR. */
  setLang(lang: AppLang): void {
    this.transitionsGelees.set(true);
    this.i18n.setLang(lang);
    if (typeof requestAnimationFrame === 'function') {
      // Deux trames : la première applique la nouvelle direction, la seconde rend la main aux
      // transitions. Dégeler dès la première rallumerait l'animation avant que le style soit posé.
      requestAnimationFrame(() => requestAnimationFrame(() => this.transitionsGelees.set(false)));
    } else {
      this.transitionsGelees.set(false);
    }
  }

  /**
   * Le geste du hamburger et de ⌘B : masquer ⇄ réafficher en desktop, ouvrir ⇄ fermer le tiroir en
   * compact. Un seul contrôle, une seule signification — « montre-moi / cache-moi la navigation ».
   */
  basculerNavigation(): void {
    if (this.compact()) {
      this.drawerOpen.update((v) => !v);
      return;
    }
    const courant = this.sidebarMode();
    if (courant === 'hidden') {
      this.appliquerMode(this.dernierModeVisible);
    } else {
      this.dernierModeVisible = courant;
      this.appliquerMode('hidden');
    }
  }

  /** Le geste du chevron : déployé ⇄ rail. Indisponible en compact (le tiroir est toujours déployé). */
  basculerRail(): void {
    this.appliquerMode(this.sidebarMode() === 'rail' ? 'expanded' : 'rail');
  }

  closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  private appliquerMode(mode: SidebarMode): void {
    this.modeChoisi.set(mode);
    if (mode !== 'hidden') this.dernierModeVisible = mode;
    try {
      localStorage.setItem(SIDEBAR_MODE_KEY, mode);
    } catch {
      // Stockage indisponible (navigation privée, quota) : le choix vaut pour la session.
    }
  }

  /** Nom de l'icône du chevron : il pointe TOUJOURS vers le geste, donc s'inverse avec la langue. */
  readonly chevronRepli = computed(() => {
    const versLeBord = this.lang() === 'ar' ? 'chevron-right' : 'chevron-left';
    const versLeContenu = this.lang() === 'ar' ? 'chevron-left' : 'chevron-right';
    return this.sidebarMode() === 'rail' ? versLeContenu : versLeBord;
  });

  /**
   * Compteurs « en attente de mon action » par clé de file (backend). Alimente les pastilles du menu.
   * Vide/0 ⇒ pas de pastille. Le serveur ne renvoie que les files pertinentes pour les rôles courants.
   */
  readonly actionCounts = signal<Record<string, number>>({});

  private loadActionCounts(): void {
    this.actionCountsApi
      .get()
      .pipe(catchError(() => of({} as Record<string, number>)), takeUntilDestroyed(this.destroyRef))
      .subscribe((counts) => this.actionCounts.set(counts ?? {}));
  }

  /**
   * Une entrée-chapeau (`queryAgnostic`) s'efface quand l'URL porte exactement la requête d'une autre
   * entrée du même écran : sur `?etape=DECISION`, seule « قرار التعيين » reste allumée (revue du
   * 2026-09-29 — deux entrées actives disaient « vous êtes à deux endroits »). Sur les autres onglets,
   * l'entrée-chapeau reste active, comme voulu le 2026-08-19.
   */
  estEclipsee(item: NavLink): boolean {
    if (!item.queryAgnostic || !item.route) {
      return false;
    }
    const arbre = this.router.parseUrl(this.url());
    const chemin = '/' + (arbre.root.children['primary']?.segments.map((x) => x.path).join('/') ?? '');
    if (chemin !== item.route) {
      return false;
    }
    const q = arbre.queryParams;
    return this.navGroups().some((g) =>
      g.items.some((autre) =>
        autre !== item && autre.route === item.route && !!autre.query
        && Object.entries(autre.query).every(([k, v]) => q[k] === v),
      ),
    );
  }

  /**
   * Une entrée `exact` cède le chemin courant quand il n'est pas le sien, ou quand une AUTRE entrée du menu,
   * plus précise, le couvre : sur `/caeep/seances/pv/…`, « محاضر اللجنة » seule ; sur
   * `/caeep/seances/programmation`, « جلسات اللجنة ». Rend la règle de surlignage de ses chemins.
   */
  cedeLeChemin(item: NavLink): boolean {
    if (!item.exact || !item.route) {
      return false;
    }
    const arbre = this.router.parseUrl(this.url());
    const chemin = '/' + (arbre.root.children['primary']?.segments.map((x) => x.path).join('/') ?? '');
    if (chemin === item.route) {
      return false;
    }
    const sous = (racine: string) => chemin === racine || chemin.startsWith(racine + '/');
    if (!sous(item.route)) {
      return true;
    }
    return this.navGroups().some((g) =>
      g.items.some((autre) =>
        autre !== item && !!autre.route && autre.route.length > item.route!.length && sous(autre.route),
      ),
    );
  }

  /** Nombre de dossiers en attente pour l'entrée (0 si pas de clé ou file vide). */
  badgeCount(item: NavLink): number {
    const c = this.actionCounts();
    if (item.badgeKeys) {
      return item.badgeKeys.reduce((n, k) => n + (c[k] ?? 0), 0);
    }
    return item.badgeKey ? (c[item.badgeKey] ?? 0) : 0;
  }

  /** L'URL courante est-elle sous l'une des racines `actifSur` de l'entrée (onglets d'une rubrique) ? */
  actifParOnglet(item: NavLink): boolean {
    if (!item.actifSur) {
      return false;
    }
    const chemin = this.url().split(/[?#]/)[0];
    return item.actifSur.some((r) => chemin === r || chemin.startsWith(r + '/'));
  }

  /** Texte de la pastille : le nombre, plafonné à « 9+ ». */
  badgeText(item: NavLink): string {
    const n = this.badgeCount(item);
    return n > 9 ? '9+' : String(n);
  }

  /** Libelle localise d'une entree de navigation selon la langue active. */
  navLabel(item: NavLink): string {
    return this.lang() === 'ar' ? item.ar : item.fr;
  }

  /** Libelle localise d'un en-tete de groupe (espace) ; vide si le groupe n'a pas d'en-tete. */
  groupLabel(group: NavGroup): string {
    if (!group.ar && !group.fr) {
      return '';
    }
    return (this.lang() === 'ar' ? group.ar : group.fr) ?? '';
  }
}

/**
 * Premier segment d'une route : `/presidence/dashboard` → `presidence`.
 *
 * <p>Sert à reconnaître l'espace courant depuis l'URL sans redéclarer un préfixe à côté de la route.
 */
function segmentDeTete(route: string): string {
  return route.split('/')[1] ?? '';
}

/**
 * L'état du menu latéral mémorisé sur le poste. Absent ⇒ {@code null} = automatique : la largeur de
 * l'écran décide tant que l'utilisateur n'a rien choisi.
 */
function lireModeSidebar(): SidebarMode | null {
  try {
    const v = localStorage.getItem(SIDEBAR_MODE_KEY);
    return v === 'expanded' || v === 'rail' || v === 'hidden' ? v : null;
  } catch {
    return null;
  }
}

/** Le mode de regroupement mémorisé sur le poste ; « par famille » par défaut (c'est la demande du CCE). */
function lireModeGroupement(): NotifGroupMode {
  try {
    return localStorage.getItem(NOTIF_GROUP_MODE_KEY) === 'date' ? 'date' : 'famille';
  } catch {
    return 'famille';
  }
}
